/** Review-Ansicht für netz-fabrik (F16): alle Meldungen mit Status-Filter.
 * Styling nach Design-Handoff (Screen 05). */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronRight, FileInput } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { datumKurz, PRIO_LABEL, STATUS_LABEL, TYP_LABEL } from "@/lib/labels"
import { api, apiError } from "@/services/api"
import { StatusKette } from "@/components/meldung/StatusKette"
import { useAuthStore } from "@/store/auth"
import type { ReportStatus } from "@/types"

const FILTER: (ReportStatus | "alle")[] = [
  "alle",
  "neu",
  "gesichtet",
  "in_umsetzung",
  "erledigt",
  "abgelehnt",
  "verworfen",
]

/**
 * testerwerk#57 (Hans 02.10.2026): Anliegen, die per E-Mail kamen, liegen
 * direkt als Issue im Repo — ohne Meldung im Tester sieht der Melder weder
 * Stand noch Rückfragen. Hier übernimmt netz-fabrik das Issue als Meldung
 * und ordnet sie dem Melder zu; danach läuft alles wie bei einer
 * freigegebenen Meldung (@kunde-Brücke, Zug, „gelöst“/„erledigt“).
 */
function IssueUebernahme() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(false)
  const [appId, setAppId] = useState<number | null>(null)
  const [issueNr, setIssueNr] = useState("")
  const [melderSub, setMelderSub] = useState("")
  const [quelle, setQuelle] = useState("")

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
    enabled: offen,
  })
  const { data: melder } = useQuery({
    queryKey: ["uebernahme", "melder", appId],
    queryFn: () => api.uebernahme.melder(appId!),
    enabled: offen && appId != null,
  })
  const anlegen = useMutation({
    mutationFn: () =>
      api.uebernahme.anlegen({
        applicationId: appId!,
        issueNr: Number(issueNr),
        melderSub,
        ...(quelle.trim() ? { quelle: quelle.trim() } : {}),
      }),
    onSuccess: (report) => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success(`Issue #${report.forgejoIssueNr} ist jetzt Meldung #${report.id}`)
      setIssueNr("")
      navigate(`/meldungen/${report.id}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const bereit = appId != null && Number(issueNr) > 0 && melderSub !== "" && !anlegen.isPending
  const feld = "rounded-lg border bg-background px-3 py-2 text-[13px]"

  return (
    <section className="mb-6 rounded-xl border bg-card p-4 shadow-card">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex w-full items-center gap-2 text-left text-[14px] font-semibold"
      >
        <ChevronRight className={`h-4 w-4 transition-transform ${offen ? "rotate-90" : ""}`} />
        <FileInput className="h-4 w-4 text-primary" />
        Meldung aus Issue übernehmen
        <span className="font-normal text-muted-foreground">
          — für Anliegen, die per E-Mail kamen und schon als Issue angelegt sind
        </span>
      </button>
      {offen && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[12px] font-semibold">
            Anwendung
            <select
              value={appId ?? ""}
              onChange={(e) => {
                setAppId(e.target.value ? Number(e.target.value) : null)
                setMelderSub("")
              }}
              className={feld}
            >
              <option value="">— wählen —</option>
              {(anwendungen ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.organisationSlug})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold">
            Issue-Nummer im Repo der Anwendung
            <input
              value={issueNr}
              onChange={(e) => setIssueNr(e.target.value.replace(/[^0-9]/g, ""))}
              inputMode="numeric"
              placeholder="z. B. 264"
              className={feld}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold">
            Melder
            <select
              value={melderSub}
              onChange={(e) => setMelderSub(e.target.value)}
              disabled={appId == null}
              className={feld}
            >
              <option value="">{appId == null ? "erst Anwendung wählen" : "— wählen —"}</option>
              {(melder ?? []).map((m) => (
                <option key={m.sub} value={m.sub}>
                  {m.name} ({m.meldungen} Meldung{m.meldungen === 1 ? "" : "en"})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold">
            Woher kam das Anliegen? (sieht der Melder)
            <input
              value={quelle}
              onChange={(e) => setQuelle(e.target.value)}
              maxLength={200}
              placeholder="z. B. E-Mail vom 01.10.2026"
              className={feld}
            />
          </label>
          <div className="flex items-center gap-3 sm:col-span-2">
            <button
              type="button"
              disabled={!bereit}
              onClick={() => anlegen.mutate()}
              className="rounded-lg bg-primary px-4 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              {anlegen.isPending ? "Übernehme …" : "Übernehmen"}
            </button>
            <span className="text-[12px] text-muted-foreground">
              Titel, Ist, Soll und Akzeptanzkriterien kommen aus dem Issue. Die Meldung steht
              danach auf „In Umsetzung“ (geschlossenes Issue: „Erledigt“) und trägt den Vermerk
              „von netz-fabrik übernommen“.
            </span>
          </div>
          <p className="text-[12px] text-muted-foreground sm:col-span-2">
            Zur Auswahl stehen Benutzer, die in dieser Organisation schon einmal gemeldet haben
            — das Testerwerk hat keine eigene Benutzerliste.
          </p>
        </div>
      )}
    </section>
  )
}

export function ReviewPage() {
  const istReviewer = useAuthStore((s) => s.isReviewer())
  const [filter, setFilter] = useState<ReportStatus | "alle">("neu")

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", "review", filter],
    queryFn: () =>
      api.reports.list({
        scope: "org",
        ...(filter !== "alle" ? { status: filter } : {}),
      }),
    enabled: istReviewer,
  })

  // Anwendungs-Spalte nur bei mehreren Anwendungen der Instanz
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
    enabled: istReviewer,
  })
  const appName = new Map((anwendungen ?? []).map((a) => [a.id, a.name]))
  const mehrereApps = (anwendungen?.length ?? 0) > 1

  if (!istReviewer) {
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
        Keine Berechtigung — die Review-Ansicht ist netz-fabrik vorbehalten.
      </p>
    )
  }

  return (
    <div>
      <h1 className="mb-1.5 text-[26px] font-bold tracking-tight">Review</h1>
      <p className="mb-5 text-sm text-muted-foreground">
        Alle Meldungen der Organisation, zur Sichtung durch netz-fabrik.
      </p>

      <IssueUebernahme />

      <div className="mb-5 flex flex-wrap gap-2">
        {FILTER.map((wert) => (
          <button
            key={wert}
            type="button"
            onClick={() => setFilter(wert)}
            className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
              filter === wert
                ? "bg-primary text-primary-foreground"
                : "border bg-card hover:border-primary/40"
            }`}
          >
            {wert === "alle" ? "Alle" : STATUS_LABEL[wert]}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-muted-foreground">Lade Meldungen …</p>}
      {error != null && (
        <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
          {apiError(error)}
        </p>
      )}

      {data && data.total === 0 && (
        <p className="rounded-xl border bg-card px-6 py-10 text-center text-muted-foreground">
          Keine Meldungen
          {filter !== "alle" ? ` im Status „${STATUS_LABEL[filter]}“` : ""}.
        </p>
      )}

      {data && data.total > 0 && (
        <div className="overflow-hidden rounded-xl border bg-card shadow-card">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-bold uppercase tracking-wide text-muted-foreground/70">
                <th className="py-3 pl-5 pr-4 font-bold">#</th>
                <th className="py-3 pr-4 font-bold">Titel</th>
                {mehrereApps && <th className="py-3 pr-4 font-bold">Anwendung</th>}
                <th className="py-3 pr-4 font-bold">Typ</th>
                <th className="py-3 pr-4 font-bold">Status</th>
                <th className="py-3 pr-4 font-bold">Priorität</th>
                <th className="py-3 pr-4 font-bold">Melder</th>
                <th className="py-3 pr-4 font-bold">Issue</th>
                <th className="py-3 pr-5 font-bold">Erstellt</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((report) => (
                <tr
                  key={report.id}
                  className="border-b last:border-b-0 hover:bg-muted/50"
                >
                  <td className="py-3.5 pl-5 pr-4 text-muted-foreground/70">{report.id}</td>
                  <td className="py-3.5 pr-4 font-semibold">
                    <Link to={`/meldungen/${report.id}`} className="text-primary hover:underline">
                      {report.titel || <em className="text-muted-foreground">ohne Titel</em>}
                    </Link>
                  </td>
                  {mehrereApps && (
                    <td className="py-3.5 pr-4">
                      {appName.get(report.applicationId) ?? report.applicationId}
                    </td>
                  )}
                  <td className="py-3.5 pr-4">{report.typ ? TYP_LABEL[report.typ] : "—"}</td>
                  <td className="py-3.5 pr-4">
                    <StatusKette report={report} kompakt />
                  </td>
                  <td className="py-3.5 pr-4">{PRIO_LABEL[report.prioritaet]}</td>
                  <td className="py-3.5 pr-4">{report.melderName || report.melderSub}</td>
                  <td className="py-3.5 pr-4">
                    {report.forgejoIssueNr ? (
                      report.forgejoIssueUrl ? (
                        <a
                          href={report.forgejoIssueUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Forgejo-Issue öffnen"
                          className="font-semibold text-primary hover:underline"
                        >
                          #{report.forgejoIssueNr}
                        </a>
                      ) : (
                        <span className="font-semibold text-primary">#{report.forgejoIssueNr}</span>
                      )
                    ) : (
                      <span className="text-muted-foreground/70">—</span>
                    )}
                  </td>
                  <td className="py-3.5 pr-5 text-muted-foreground">
                    {datumKurz(report.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
